import { useVisualBinding } from "@judo/core";
import type { Formatted } from "@judo/model-api";
import Typography from "@mui/material/Typography";

export interface FormattedRendererProps {
	element: Formatted;
	readOnly?: boolean;
}

/**
 * Apply sprintf-style format string to a value.
 * Supports %s (string), %d (integer), %f (float), and %% (literal %).
 * Falls back to String(value) if format is not provided.
 */
function applyFormat(format: string | undefined, value: unknown): string {
	if (value == null) {
		return "";
	}

	if (!format) {
		return String(value);
	}

	// Replace format specifiers with the value
	return format.replace(/%([%sdf])/g, (match, specifier) => {
		if (specifier === "%") {
			return "%";
		}
		if (specifier === "d") {
			const num = Number(value);
			return Number.isNaN(num) ? String(value) : String(Math.trunc(num));
		}
		if (specifier === "f") {
			const num = Number(value);
			return Number.isNaN(num) ? String(value) : String(num);
		}
		// %s - string
		return String(value);
	});
}

/**
 * Renderer for Formatted visual elements.
 * Displays attribute value using a sprintf-style format string.
 * Uses useVisualBinding to read from the data store.
 */
export function FormattedRenderer({ element }: FormattedRendererProps) {
	const binding = useVisualBinding(element);

	if (binding.hidden) {
		return null;
	}

	const formatted = applyFormat(element.format, binding.value);
	const testId = `formatted::${element.sourceId ?? element["xmi:id"]}`;

	if (element.isMultiLine) {
		return (
			<Typography component="div" data-testid={testId} sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
				{formatted}
			</Typography>
		);
	}

	return <Typography data-testid={testId}>{formatted}</Typography>;
}
